import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, Zap, Shield, Building2, Users, ArrowRight, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import WaitlistDialog from "../components/WaitlistDialog";
import EarlyAccessDialog from "../components/EarlyAccessDialog";
import Footer from "../components/Footer";

const PLAN_ICONS = {
  agency_starter: Sparkles, agency_growth: Zap, agency_pro: Shield, agency_enterprise: Building2,
  family_portal: Users, subcontractor: Users,
};

const BILLING_TIER_IDS = []; // legacy — billing-module tiers retired in V1

const AUDIENCE_TITLES = {
  agency: "For Home-Care Agencies",
  family: "For Families",
  subcontractor: "For Subcontractors",
};

function fmtPrice(n) {
  return n % 1 === 0 ? `$${n.toLocaleString()}` : `$${n.toFixed(2)}`;
}

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [waitlistAddon, setWaitlistAddon] = useState(null);
  const [trialDays, setTrialDays] = useState(0);
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [earlyAccessPlan, setEarlyAccessPlan] = useState(null);

  const isAuthed = user && user !== false && user !== null;
  const isLoadingAuth = user === null;

  useEffect(() => {
    if (isLoadingAuth) return; // wait for auth context to resolve
    const endpoint = isAuthed ? "/billing/plans" : "/billing/plans/public";
    api.get(endpoint)
      .then((r) => {
        setPlans(r.data.plans);
        setTrialDays(r.data.trial_days || 0);
        setBillingEnabled(r.data.billing_enabled !== false);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [isAuthed, isLoadingAuth]);

  const mainPlans = useMemo(() => plans.filter((p) => p.kind === "main"), [plans]);
  const addons = useMemo(() => plans.filter((p) => p.kind === "addon"), [plans]);

  const agencyPlans = useMemo(() => mainPlans.filter((p) => p.audience === "agency"), [mainPlans]);
  const familyPlans = useMemo(() => mainPlans.filter((p) => p.audience === "family"), [mainPlans]);
  const subPlans = useMemo(() => mainPlans.filter((p) => p.audience === "subcontractor"), [mainPlans]);

  const canHaveAddons = isAuthed && (user.role === "agency" || user.role === "admin" || user.role === "subcontractor");

  function toggleAddon(planId, addonId) {
    setSelectedAddons((prev) => {
      const set = new Set(prev[planId] || []);
      if (set.has(addonId)) set.delete(addonId);
      else {
        if (BILLING_TIER_IDS.includes(addonId)) BILLING_TIER_IDS.forEach((id) => set.delete(id));
        set.add(addonId);
      }
      return { ...prev, [planId]: set };
    });
  }

  async function subscribe(planId) {
    // Pre-launch mode: subscriptions are paused. Open the early-access waitlist.
    if (!billingEnabled) {
      const plan = plans.find((p) => p.id === planId);
      setEarlyAccessPlan(plan?.name || planId);
      return;
    }
    if (!isAuthed) {
      // Save intent and send to auth
      try { localStorage.setItem("nexari_intent_plan", planId); } catch (e) { /* noop */ }
      toast.info("Create your free account to continue to checkout.");
      navigate("/auth");
      return;
    }
    setSubmitting(planId);
    try {
      const addonIds = Array.from(selectedAddons[planId] || []);
      const res = await api.post("/billing/checkout", {
        plan_id: planId, addon_ids: addonIds, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSubmitting(null);
    }
  }

  const content = (
    <div data-testid="pricing-page" className="space-y-16">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Pricing</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-[#1C211F]">
          Plans for every <span className="font-bold text-[#2E5C50]">agency size</span>
        </h1>
        <p className="mt-4 text-base text-[#57605A]">
          Workforce compliance automation. Track every credential. Get reminded before expiration.
          Generate audit-ready packets. Cancel anytime.
        </p>
        {!billingEnabled && (
          <div data-testid="prelaunch-banner" className="mt-5 inline-flex items-center gap-2 bg-[#FFF4E6] text-[#1C211F] border border-[#C07A60]/30 px-4 py-2 rounded-full text-sm font-medium">
            <Clock className="w-3.5 h-3.5 text-[#C07A60]" strokeWidth={2} />
            Pre-launch · Subscriptions open soon — <strong className="text-[#C07A60]">join the early-access list</strong>
          </div>
        )}
        {trialDays > 0 && billingEnabled && (
          <div data-testid="trial-banner" className="mt-5 inline-flex items-center gap-2 bg-[#E6F0EB] text-[#1C211F] border border-[#34745C]/20 px-4 py-2 rounded-full text-sm font-medium">
            <Sparkles className="w-3.5 h-3.5 text-[#2E5C50]" strokeWidth={2} />
            Start with a <strong className="text-[#2E5C50]">{trialDays}-day free trial</strong> — no charge until day {trialDays + 1}
          </div>
        )}
        {isAuthed && (
          <button
            data-testid="pricing-manage-billing-btn"
            onClick={() => navigate("/billing")}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#2E5C50] hover:underline"
          >
            Manage existing subscription <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* AGENCIES */}
      {agencyPlans.length > 0 && (
        <section>
          <h2 className="text-center font-heading text-2xl text-[#1C211F]">
            {AUDIENCE_TITLES.agency}
          </h2>
          <p className="text-center mt-2 text-sm text-[#57605A]">
            For home-care agencies — track every employee credential, automate renewal reminders, generate audit packets.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {agencyPlans.map((p) => {
              const Icon = PLAN_ICONS[p.id] || Sparkles;
              const isPro = p.id === "agency_pro";
              const isEnterprise = !!p.contact_sales;
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.id}`}
                  className={`relative bg-white rounded-xl border ${isPro ? "border-[#2E5C50] shadow-lg" : "border-[#E8E6E1]"} p-6 flex flex-col`}
                >
                  {isPro && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2E5C50] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <div className="w-10 h-10 bg-[#D6E0D9]/50 rounded-md flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-lg text-[#1C211F]">{p.name}</h3>
                  <p className="mt-1 text-sm text-[#57605A] min-h-[40px]">{p.description}</p>
                  <div className="mt-4">
                    <span className="text-3xl font-light tracking-tight text-[#1C211F]">{fmtPrice(p.amount)}</span>
                    <span className="text-sm text-[#57605A]">/mo</span>
                    {isEnterprise && <p className="text-xs text-[#57605A] mt-1">Starts here · custom up to $5,000+</p>}
                  </div>
                  <button
                    data-testid={`subscribe-${p.id}-btn`}
                    onClick={() => subscribe(p.id)}
                    disabled={submitting === p.id}
                    className={`mt-6 flex items-center justify-center gap-2 ${isPro ? "bg-[#2E5C50] text-white hover:bg-[#22463D]" : "bg-[#F9F7F4] text-[#1C211F] hover:bg-[#E8E6E1]"} border border-transparent transition-colors duration-200 rounded-md py-2.5 font-medium text-sm`}
                  >
                    {submitting === p.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>{!billingEnabled ? "Join early access" : (trialDays > 0 ? "Start free trial" : (isAuthed ? "Subscribe" : "Get started"))} <ArrowRight className="w-3.5 h-3.5" /></>
                    )}
                  </button>
                  {isEnterprise && (
                    <a
                      href="mailto:sales@nexarihealth.com?subject=Enterprise%20Inquiry"
                      data-testid={`contact-sales-${p.id}-btn`}
                      className="mt-2 text-center text-xs font-medium text-[#2E5C50] hover:underline"
                    >
                      Or contact sales for custom pricing
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* FAMILY + SUBCONTRACTOR */}
      {(familyPlans.length > 0 || subPlans.length > 0) && (
        <section className="grid md:grid-cols-2 gap-8">
          {familyPlans.length > 0 && (
            <div>
              <h2 className="font-heading text-2xl text-[#1C211F]">{AUDIENCE_TITLES.family}</h2>
              <p className="mt-2 text-sm text-[#57605A]">
                For families staying informed about a loved one&apos;s care.
              </p>
              {familyPlans.map((p) => (
                <div key={p.id} data-testid={`plan-card-${p.id}`}
                     className="mt-4 bg-white rounded-xl border border-[#E8E6E1] p-6 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-[#1C211F]">{p.name}</h3>
                    <p className="mt-1 text-sm text-[#57605A]">{p.description}</p>
                    <div className="mt-2">
                      <span className="text-2xl font-light text-[#1C211F]">{fmtPrice(p.amount)}</span>
                      <span className="text-sm text-[#57605A]">/mo</span>
                    </div>
                  </div>
                  <button
                    data-testid={`subscribe-${p.id}-btn`}
                    onClick={() => subscribe(p.id)}
                    disabled={submitting === p.id}
                    className="ml-4 flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-4 py-2.5 font-medium text-sm whitespace-nowrap"
                  >
                    {submitting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{!billingEnabled ? "Join early access" : (trialDays > 0 ? "Start free trial" : (isAuthed ? "Subscribe" : "Get started"))} <ArrowRight className="w-3.5 h-3.5" /></>}
                  </button>
                </div>
              ))}
            </div>
          )}
          {subPlans.length > 0 && (
            <div>
              <h2 className="font-heading text-2xl text-[#1C211F]">{AUDIENCE_TITLES.subcontractor}</h2>
              <p className="mt-2 text-sm text-[#57605A]">
                For independent subcontractors running their own caregiver roster.
              </p>
              {subPlans.map((p) => (
                <div key={p.id} data-testid={`plan-card-${p.id}`}
                     className="mt-4 bg-white rounded-xl border border-[#E8E6E1] p-6 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-[#1C211F]">{p.name}</h3>
                    <p className="mt-1 text-sm text-[#57605A]">{p.description}</p>
                    <div className="mt-2">
                      <span className="text-2xl font-light text-[#1C211F]">{fmtPrice(p.amount)}</span>
                      <span className="text-sm text-[#57605A]">/mo</span>
                    </div>
                  </div>
                  <button
                    data-testid={`subscribe-${p.id}-btn`}
                    onClick={() => subscribe(p.id)}
                    disabled={submitting === p.id}
                    className="ml-4 flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-4 py-2.5 font-medium text-sm whitespace-nowrap"
                  >
                    {submitting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{!billingEnabled ? "Join early access" : (trialDays > 0 ? "Start free trial" : (isAuthed ? "Subscribe" : "Get started"))} <ArrowRight className="w-3.5 h-3.5" /></>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Add-ons (only for authed agency/sub users) */}
      {canHaveAddons && addons.length > 0 && (
        <section className="border-t border-[#E8E6E1] pt-12">
          <div className="text-center">
            <h2 className="font-heading font-semibold text-2xl text-[#1C211F]">Add-on modules</h2>
            <p className="mt-2 text-sm text-[#57605A]">
              Select live add-ons during checkout, or join the waitlist for ones coming soon.
            </p>
          </div>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {addons.map((a) => (
              <div key={a.id} data-testid={`addon-card-${a.id}`}
                   className={`relative bg-white border ${a.coming_soon ? "border-dashed border-[#C07A60]/40" : "border-[#E8E6E1]"} rounded-lg p-5 flex flex-col`}>
                {a.coming_soon && (
                  <span data-testid={`coming-soon-badge-${a.id}`} className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#FDF3E1] text-[#C07A60] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
                    <Clock className="w-2.5 h-2.5" strokeWidth={2} /> Coming soon
                  </span>
                )}
                <h3 className="font-heading font-semibold text-base text-[#1C211F] pr-20">{a.name}</h3>
                <p className="mt-1 text-xs text-[#57605A] min-h-[36px]">{a.description}</p>
                <div className="mt-3 mb-3">
                  <span className="text-lg font-semibold text-[#1C211F]">{fmtPrice(a.amount)}</span>
                  <span className="text-xs text-[#57605A]">/mo</span>
                </div>
                <div className="mt-auto border-t border-[#F0EDE8] pt-3">
                  {a.coming_soon ? (
                    <button
                      data-testid={`join-waitlist-${a.id}-btn`}
                      onClick={() => setWaitlistAddon(a)}
                      className="w-full bg-[#F9F7F4] text-[#1C211F] hover:bg-[#E8E6E1] transition-colors duration-200 rounded-md py-2 text-xs font-medium border border-[#E8E6E1]"
                    >
                      Join waitlist
                    </button>
                  ) : (
                    <div className="space-y-1.5">
                      {mainPlans.filter((p) => p.audience === "agency").map((p) => {
                        const checked = selectedAddons[p.id]?.has(a.id);
                        return (
                          <label key={p.id} data-testid={`addon-toggle-${a.id}-${p.id}`}
                                 className="flex items-center gap-2 text-xs text-[#57605A] cursor-pointer hover:text-[#1C211F]">
                            <input type="checkbox" checked={!!checked}
                                   onChange={() => toggleAddon(p.id, a.id)}
                                   className="w-3.5 h-3.5 accent-[#2E5C50]" />
                            Bundle with {p.name.replace("Agency • ", "")}
                            {checked && <Check className="w-3 h-3 text-[#2E5C50] ml-auto" />}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!isAuthed && addons.length > 0 && (
        <section className="border-t border-[#E8E6E1] pt-12">
          <h2 className="text-center font-heading font-semibold text-2xl text-[#1C211F]">Optional add-ons</h2>
          <p className="text-center mt-2 text-sm text-[#57605A] max-w-2xl mx-auto">
            Bundle these with any main plan during checkout. Some modules are still in development — join the waitlist to be the first to know.
          </p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {addons.map((a) => (
              <div key={a.id} data-testid={`addon-card-${a.id}`}
                   className={`relative bg-white border ${a.coming_soon ? "border-dashed border-[#C07A60]/40" : "border-[#E8E6E1]"} rounded-lg p-5 flex flex-col`}>
                {a.coming_soon && (
                  <span data-testid={`coming-soon-badge-${a.id}`} className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#FDF3E1] text-[#C07A60] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
                    <Clock className="w-2.5 h-2.5" strokeWidth={2} /> Coming soon
                  </span>
                )}
                <h3 className="font-heading font-semibold text-base text-[#1C211F] pr-20">{a.name}</h3>
                <p className="mt-1 text-xs text-[#57605A] min-h-[36px]">{a.description}</p>
                <div className="mt-3 mb-3">
                  <span className="text-lg font-semibold text-[#1C211F]">{fmtPrice(a.amount)}</span>
                  <span className="text-xs text-[#57605A]">/mo</span>
                </div>
                {a.coming_soon && (
                  <button
                    data-testid={`join-waitlist-${a.id}-btn`}
                    onClick={() => setWaitlistAddon(a)}
                    className="mt-auto w-full bg-[#F9F7F4] text-[#1C211F] hover:bg-[#E8E6E1] transition-colors duration-200 rounded-md py-2 text-xs font-medium border border-[#E8E6E1]"
                  >
                    Join waitlist
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="text-center text-xs text-[#57605A] pt-4">
        {billingEnabled ? "Powered by Stripe · Secure checkout · Cancel anytime" : "Pre-launch — join the early-access list to be notified the moment subscriptions open."}
      </div>
      <WaitlistDialog addon={waitlistAddon} onClose={() => setWaitlistAddon(null)} />
      <EarlyAccessDialog open={!!earlyAccessPlan} interestedPlan={earlyAccessPlan} onClose={() => setEarlyAccessPlan(null)} />
    </div>
  );

  if (loading || isLoadingAuth) {
    return (
      <PublicShell isAuthed={isAuthed}>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" />
        </div>
      </PublicShell>
    );
  }

  return isAuthed ? <Layout>{content}</Layout> : <PublicShell>{content}</PublicShell>;
}

// Public shell for prospects (no Layout, no auth-gated nav)
function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <nav className="sticky top-0 z-50 bg-[#F9F7F4]/80 backdrop-blur-xl border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="public-nav-brand" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#2E5C50] rounded-md flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">Nexari Health</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/pricing"
              data-testid="public-nav-pricing"
              className="hidden sm:inline-flex text-sm font-medium text-[#2E5C50] px-3 py-1.5"
            >
              Pricing
            </Link>
            <Link
              to="/auth"
              data-testid="public-nav-signin"
              className="bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2 text-sm font-medium"
            >
              Sign in
            </Link>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>
      <Footer />
    </div>
  );
}
